import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queryClient } from '../components/queryClient';
import { useHasPermission } from '../hooks/useHasPermission';
import { BulkEventEditActionDetails, BulkEventEditJobsListResponse } from '../interfaces';
import { BulkEventEditJobService } from '../services';

const QUERY_KEY = 'bulk-event-edit-jobs';

const ACTIVE_JOB_POLL_INTERVAL = 10000;

const hasActiveJobs = (data?: BulkEventEditJobsListResponse) =>
  data?.jobs.some((job) => job.job_status === 'PENDING' || job.job_status === 'RUNNING') ?? false;

export const useBulkEventEditJobsQuery = (limit: number, offset: number, statusFilter?: string) => {
  const canView = useHasPermission('bulk_event_edit_jobs:read:list');

  const queryResult = useQuery<BulkEventEditJobsListResponse>({
    queryKey: [QUERY_KEY, limit, offset, statusFilter],
    queryFn: () => BulkEventEditJobService.listJobs(limit, offset, statusFilter),
    enabled: canView,
    refetchInterval: (data) => (hasActiveJobs(data) ? ACTIVE_JOB_POLL_INTERVAL : false),
  });

  // Accumulate all pages loaded so far for the current filter
  const allJobs = useMemo(() => {
    const jobs: BulkEventEditJobsListResponse['jobs'] = [];
    const pages = Math.floor(offset / limit) + 1;
    for (let page = 0; page < pages; page++) {
      const pageOffset = page * limit;
      const cached = queryClient.getQueryData<BulkEventEditJobsListResponse>([QUERY_KEY, limit, pageOffset, statusFilter]);
      if (cached?.jobs) {
        jobs.push(...cached.jobs);
      }
    }

    return jobs;
  }, [limit, offset, statusFilter, queryResult.data]);

  return { ...queryResult, allJobs };
};

export const forceRefreshBulkEventEditJobs = () => {
  queryClient.invalidateQueries([QUERY_KEY]);
};

const ACTIONS_QUERY_KEY = 'bulk-event-edit-actions';

export const useBulkEventEditActionsQuery = () => {
  const canView = useHasPermission('bulk_event_edit_jobs:read:list');

  return useQuery<BulkEventEditActionDetails[]>({
    queryKey: [ACTIONS_QUERY_KEY],
    queryFn: () => BulkEventEditJobService.getActions(),
    enabled: canView,
    staleTime: Infinity,
  });
};

